import React, { MouseEvent, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { PermissionName } from "@mapmycustomers/shared";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { hasPermission, isCurrentUserReadOnly } from "@app/store/iam";

import styles from "./useEmailSendVariants.module.scss";

const messages = defineMessages({
  copyEmail: {
    id: "utils.email.sendVariants.copyEmail",
    defaultMessage: "Copy Email",
    description: "Copy Email",
  },
  copyTooltip: {
    id: "utils.email.sendVariants.copy.tooltip.title",
    defaultMessage: "{copied, select, true {Copied to clipboard} other {Copy to clipboard}}",
    description: "Tooltip for the Copy email button",
  },
  sendInMyEmailClient: {
    id: "utils.email.sendVariants.sendInMyEmailClient",
    defaultMessage: "Send in my email client",
    description: "Send in my email client",
  },
  sendViaMMC: {
    id: "utils.email.sendVariants.sendViaMMC",
    defaultMessage: "Send via MMC",
    description: "Send via MMC email",
  },
});

interface UseEmailSendVariantsArgs {
  allowSendViaEmailClient?: boolean;
  allowSendViaMmcEmail?: boolean;
  email: string;
  isSendViaMmcLoading?: boolean;
  onSendViaEmailClient?: () => void;
  onSendViaMmcEmail?: () => void;
}

const useEmailSendVariants = ({
  allowSendViaEmailClient = true,
  allowSendViaMmcEmail = true,
  email,
  isSendViaMmcLoading,
  onSendViaEmailClient,
  onSendViaMmcEmail,
}: UseEmailSendVariantsArgs) => {
  const intl = useIntl();

  const [copied, setCopied] = useState(false);

  const handleCopyEmail = useCallback(
    async (e: MouseEvent<HTMLDivElement>) => {
      stopEvents(e);
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
    [email]
  );

  const disabled = isSendViaMmcLoading;

  const canSendEmails = useSelector(hasPermission)(PermissionName.EMAILS_CREATE);
  const isReadOnlyMember = useSelector(isCurrentUserReadOnly);

  return useMemo(() => {
    return [
      allowSendViaMmcEmail && canSendEmails
        ? {
            disabled,
            key: "sendViaMMCEmail",
            label: (
              <span className={styles.labelWithLoadingSpinner}>
                <span>{intl.formatMessage(messages.sendViaMMC)}</span>
                {isSendViaMmcLoading && (
                  <LoadingSpinner className={styles.loadingSpinner} inline mini />
                )}
              </span>
            ),
            onClick: onSendViaMmcEmail,
          }
        : undefined,
      allowSendViaEmailClient
        ? {
            disabled: isReadOnlyMember || disabled,
            key: "sendInMyEmailClient",
            label: intl.formatMessage(messages.sendInMyEmailClient),
            onClick: onSendViaEmailClient,
          }
        : undefined,
      {
        disabled,
        key: "copyEmail",
        label: (
          <Tooltip placement="topLeft" title={intl.formatMessage(messages.copyTooltip, { copied })}>
            <div onClick={handleCopyEmail}>{intl.formatMessage(messages.copyEmail)}</div>
          </Tooltip>
        ),
      },
    ].filter(isDefined);
  }, [
    allowSendViaEmailClient,
    allowSendViaMmcEmail,
    canSendEmails,
    copied,
    disabled,
    handleCopyEmail,
    intl,
    isReadOnlyMember,
    isSendViaMmcLoading,
    onSendViaEmailClient,
    onSendViaMmcEmail,
  ]);
};

export default useEmailSendVariants;
